import { useId, type ReactNode } from 'react';

import { defineCatalogue } from '../../i18n';
import { Field, FieldDescription, FieldError, FieldLabel } from '@/ui/field';
import { Input } from '@/ui/input';
import { Label } from '@/ui/label';
import { RadioGroup, RadioGroupItem } from '@/ui/radio-group';

export type Sign = 'add' | 'subtract';

const amountCatalogue = defineCatalogue(
  { 'amount.label': 'Quantidade', 'sign.add': 'Adicionar', 'sign.subtract': 'Subtrair' },
  { 'amount.label': 'Amount', 'sign.add': 'Add', 'sign.subtract': 'Subtract' },
);
const t = amountCatalogue.t;

/**
 * The sign is a control, not a character: a minus typed into a number field
 * is invisible at a glance and survives a copy-paste, so a signed amount is
 * chosen from a radio pair rather than typed. `sign: null` drops the pair
 * entirely -- a grant is positive-only and has no sign to choose.
 */
export function AmountField(props: {
  sign: Sign | null;
  onSignChange?: (sign: Sign) => void;
  value: number | null;
  onChange: (value: number | null) => void;
  hint?: string;
  error?: string | null;
}): ReactNode {
  const amountId = useId();
  const errorId = `${amountId}-error`;

  // A grant never has a sign to choose and passes no `onSignChange` at all
  // (its `sign` stays `null` for the whole dialog's life); an adjustment
  // does, even before anything is picked -- so the pair's presence is keyed
  // on the callback, not on whether something is currently selected.
  return (
    <div className="grid gap-3">
      {props.onSignChange && (
        <RadioGroup
          // `''`, not `undefined`: `undefined` leaves the group owning its own
          // selection, and the first pick then switches it to controlled --
          // `RetireKeyDialog`'s grace picker is spelled the same way.
          value={props.sign ?? ''}
          onValueChange={(value) => props.onSignChange?.(value as Sign)}
          className="flex flex-row gap-4"
        >
          <div className="flex items-center gap-2">
            <RadioGroupItem value="add" id={`${amountId}-add`} />
            <Label htmlFor={`${amountId}-add`}>{t('sign.add')}</Label>
          </div>
          <div className="flex items-center gap-2">
            <RadioGroupItem value="subtract" id={`${amountId}-subtract`} />
            <Label htmlFor={`${amountId}-subtract`}>{t('sign.subtract')}</Label>
          </div>
        </RadioGroup>
      )}
      <Field data-invalid={props.error ? true : undefined}>
        <FieldLabel htmlFor={amountId}>{t('amount.label')}</FieldLabel>
        <Input
          id={amountId}
          type="number"
          step={1}
          value={props.value ?? ''}
          aria-invalid={props.error ? true : undefined}
          aria-describedby={props.error ? errorId : undefined}
          onChange={(event) => {
            const raw = event.target.value;
            // Step of 1 keeps the spinner integer; a typed decimal is one
            // keystroke away, so truncate rather than trust it -- money is an
            // integer everywhere in this estate.
            props.onChange(raw === '' ? null : Math.trunc(Number(raw)));
          }}
        />
        {props.hint !== undefined && <FieldDescription>{props.hint}</FieldDescription>}
        <FieldError id={errorId}>{props.error}</FieldError>
      </Field>
    </div>
  );
}
